import type {
  PropGraphPort,
  PropGraphPortKind,
  PropGraphValueType,
  PropNodeType,
} from "../../shared/types/PropGraph";
import type { PortDirection } from "./types";
import { PropGraphDocsArticle } from "./styles";

export function PropGraphPortDocument({ activeKey }: { activeKey: string }) {
  return (
    <PropGraphDocsArticle>
      <p>
        输出端口在运行时不是一份静态数据，而是一个可求值函数。输入端口连接到输出端口后，
        运行器会在需要该输入时调用上游函数，并取得该输出端口声明的数据类型，例如目标数组、整数、浮点数、条件、品质或标签。
      </p>
      <h2>端口、参数文档</h2>
      <p>
        端口定义节点之间可以传递的数据类型。端口类型必须匹配，目标端口支持目标数组语义；
        品质、标签、整数、浮点数和条件端口各自独立，避免运行时出现隐式转换。
      </p>
      {(activeKey === "ports" || activeKey === "ports-types") && (
        <>
          <h3>端口类型</h3>
          <table>
            <tbody>
              <tr><th>流程</th><td>控制动作执行顺序。事件输出流程，动作接收流程并输出后续分支。</td></tr>
              <tr><th>目标</th><td>道具实例数组。单个目标也按数组传递，便于动作统一处理。</td></tr>
              <tr><th>整数 / 浮点数</th><td>用于数量、倍率、概率、计算结果和属性读取。</td></tr>
              <tr><th>条件</th><td>布尔值，连接到动作条件端口后控制动作是否触发。</td></tr>
              <tr><th>品质 / 标签</th><td>配置值端口，用于品质筛选和标签筛选。</td></tr>
            </tbody>
          </table>
        </>
      )}
      {(activeKey === "ports" || activeKey === "ports-connect") && (
        <>
          <h3>连接规则</h3>
          <ul>
            <li>输出端口只能连接到兼容的输入端口。</li>
            <li>未标记为可多连的输入端口只能保留一条输入连接。</li>
            <li>动作的目标端口、条件端口和部分原型端口支持多连。</li>
            <li>连线不保存显示标签，语义由端口类型和端口名决定。</li>
          </ul>
        </>
      )}
      {(activeKey === "ports" || activeKey === "ports-action-params") && (
        <>
          <h3>动作参数</h3>
          <table>
            <tbody>
              <tr><th>负面</th><td>标记动作产生负面效果，净化逻辑会据此识别并移除。</td></tr>
              <tr><th>自身特效</th><td>动作触发时在当前道具自身播放的特效，运行时编译为资源路径。</td></tr>
              <tr><th>目标特效</th><td>动作作用到目标时播放的特效，支持多选。</td></tr>
              <tr><th>互斥标签</th><td>永久加成的同类覆盖标识，相同互斥标签不叠加。</td></tr>
              <tr><th>间隔 / 循环</th><td>事件参数，用于控制回合开始事件触发频率和次数。</td></tr>
            </tbody>
          </table>
        </>
      )}
      {(activeKey === "ports" || activeKey === "ports-validation") && (
        <>
          <h3>校验规则</h3>
          <ul>
            <li>必填参数为空时应记录规则错误，避免运行时得到不确定结果。</li>
            <li>必填输入端口未连接时应记录规则错误。</li>
            <li>节点类型创建后固定，避免旧连线在类型切换后语义失效。</li>
            <li>运行时图只保留游戏执行所需数据。</li>
          </ul>
        </>
      )}
    </PropGraphDocsArticle>
  );
}

export function getPropGraphPortDocumentLabel(port: PropGraphPort) {
  const labels: Record<string, string> = {
    flow: "流程",
    complete: "流程-完成",
    fail: "流程-失败",
    each: "流程-每次",
    targets: "目标",
    target: "目标",
    prototypes: "目标-转化",
    prototype: "原型",
    position: "位置",
    positions: "位置",
    amount: "数量",
    multiplier: "倍率",
    condition: "条件",
    rarity: "品质",
    tag: "标签",
    count: "数量",
    min: "最小值",
    max: "最大值",
    left: "左值",
    right: "右值",
    value: "值",
  };
  return labels[port.id] ?? port.name ?? port.id;
}

export function getPropGraphPortKindDocumentLabel(kind: PropGraphPortKind) {
  const labels: Record<PropGraphPortKind, string> = {
    flow: "流程",
    value: "数值",
    target: "目标",
    condition: "条件",
    rarity: "品质",
    tag: "标签",
  };
  return labels[kind];
}

export function getPropGraphValueTypeDocumentLabel(type: PropGraphValueType | undefined) {
  if (!type) return "-";
  const labels: Record<PropGraphValueType, string> = {
    flow: "流程",
    propArray: "道具数组",
    integer: "整数",
    float: "浮点数",
    boolean: "布尔",
    probability: "概率",
    multiplier: "倍率",
    rarity: "品质",
    tag: "标签",
  };
  return labels[type];
}

export function getPropGraphPortRequiredText(port: PropGraphPort) {
  if (port.required === false) return port.multiple ? "可选，多连" : "可选";
  return port.multiple ? "必填，多连" : "必填";
}

const propGraphPortSpecificDescriptions: Record<string, string> = {
  "eventTurnStart:output:flow": "回合开始事件满足间隔和循环限制后调用该流程函数，启动本次回合的后续效果。",
  "eventAcquire:output:flow": "本道具处于持有状态时每轮调用该流程函数；道具被移除后停止调用。",
  "eventUse:output:flow": "玩家主动使用本道具后调用该流程函数，启动辅料或主动效果。",
  "eventEliminated:input:target": "提供需要监听的目标函数；每轮开始求值该函数，并为返回的目标注册本轮消除监听。",
  "eventEliminated:output:flow": "本轮监听目标被消除后调用该流程函数，启动消除后的后续效果；回合结束后监听会注销。",

  "targetSelf:output:target": "返回当前道具实例数组，通常用于让动作作用于自身。",
  "targetBoard:output:targets": "根据范围和位置参数返回盘面原料数组。",
  "prototypeLibrary:output:prototypes": "根据类型参数返回原料、辅料或配方的原型数组。",
  "prototypeSelect:output:prototypes": "返回参数指定的固定道具原型数组。",

  "filterRarity:input:rarity": "提供允许保留的品质函数；可多连表示多个品质候选。",
  "filterTag:input:tag": "提供允许保留的标签函数；可多连表示多个标签候选。",
  "filterPrototype:input:prototype": "提供允许保留的原型函数；筛选时比较实例的原型 ID。",
  "filterRandom:input:count": "提供随机保留数量函数。允许 0 或正整数；0 返回空目标数组。",

  "actionGrow:input:targets": "提供要永久改变魔力产出的目标数组函数。",
  "actionGrow:input:amount": "可选端口。提供每个目标永久增加或减少的魔力产出值，允许有限整数。",
  "actionExtraYield:input:targets": "提供额外产出作用的目标数组函数。",
  "actionExtraYield:input:amount": "可选端口。提供每个目标本次额外增加或减少的魔力值，允许有限整数。",
  "actionExtraYield:input:multiplier": "可选端口。提供本次产出的倍率修正值，允许有限浮点数。",
  "actionPermanentBoost:input:targets": "提供获得持续产出加成的目标数组函数。",
  "actionPermanentBoost:input:amount": "可选端口。提供目标此后产出的固定加成值，允许有限整数。",
  "actionPermanentBoost:input:multiplier": "可选端口。提供目标此后产出的倍率加成值，允许有限浮点数。",
  "actionPurify:input:targets": "提供需要移除负面效果的目标数组函数。",
  "actionProtect:input:targets": "提供需要保护、避免被消除的目标数组函数。",
  "actionAccelerate:input:targets": "提供需要改变倒计时的目标数组函数。",
  "actionAccelerate:input:amount": "提供倒计时变化值。负数表示倒计时减少，正数表示倒计时延后，0 表示不变化。",
  "actionTransform:input:targets": "提供要被转化的目标数组函数。",
  "actionTransform:input:prototypes": "提供转化后的目标原型函数；可多连作为随机候选。",
  "actionSpawn:input:targets": "提供要生成的目标或道具原型。",
  "actionSpawn:input:position": "提供生成位置候选函数；为空时由运行器选择可用位置。",
  "actionEliminate:input:targets": "提供要消除的目标数组函数。",
  "actionEliminate:input:amount": "可选端口。提供最多消除的目标数量；未连接时按动作默认数量处理。",
  "actionCollect:input:targets": "提供被收集的目标；每个目标提供指定数量的可收获魔力，目标自身不会减少产出。",
  "actionHarvest:input:flow": "接收使用流程，收到后释放当前道具已收集的魔力。",
  "actionRarityWeight:input:rarity": "提供需要调整抽取权重的品质函数。",
  "actionRarityWeight:input:multiplier": "提供该品质抽取权重的倍率函数。0 表示权重归零，1 表示不变，大于 1 表示提高权重。",
  "actionExtendCommission:input:flow": "接收上游流程；流程到达后增加当前委托调和次数。",
  "actionSwapPosition:input:targets": "提供需要被移动或交换的目标数组函数。",
  "actionSwapPosition:input:positions": "提供目标要交换到的位置候选函数。",

  "intAttribute:input:target": "提供读取整数属性的目标函数；通常只读取第一个目标。",
  "floatAttribute:input:target": "提供读取浮点属性的目标函数；通常只读取第一个目标。",
  "intQuantity:input:targets": "提供需要统计数量的目标数组函数。",
  "intKindCount:input:targets": "提供需要统计种类数的目标数组函数。",
  "intRandom:input:min": "提供整数随机范围的最小值函数。",
  "intRandom:input:max": "提供整数随机范围的最大值函数。",
  "floatRandom:input:min": "提供浮点随机范围的最小值函数。",
  "floatRandom:input:max": "提供浮点随机范围的最大值函数。",
  "roundNumber:input:value": "提供需要取整的浮点数函数。",
  "logicContains:input:targets": "提供被检查的目标数组函数。",
  "logicContains:input:target": "提供要判断是否存在于目标数组中的目标函数。",
};

export function getPropGraphPortSpecificDescription(
  type: PropNodeType,
  port: PropGraphPort,
  direction: PortDirection,
) {
  return propGraphPortSpecificDescriptions[`${type}:${direction}:${port.id}`];
}

export function getPropGraphPortDocumentDescription(type: PropNodeType, port: PropGraphPort, direction: PortDirection) {
  const specific = getPropGraphPortSpecificDescription(type, port, direction);
  if (specific) return specific;

  if (direction === "output") {
    if (port.id === "complete") return "对应动作完成流程函数；动作成功触发且全部完成后，运行时调用该函数继续执行。";
    if (port.id === "fail") return "对应动作失败流程函数；动作未触发或没有可作用目标时，运行时调用该函数继续执行。";
    if (port.id === "each") return "对应逐目标流程函数；动作处理多个目标时，每个目标成功处理后调用一次。";
    if (port.id === "flow") return "对应流程函数；事件满足触发条件后，运行时调用该函数启动后续动作链。";
    if (port.kind === "target") return "对应目标函数；下游输入端口求值时调用，返回目标数组或原型数组。";
    if (port.kind === "value") return "对应数值函数；下游输入端口求值时调用，返回整数、浮点数、概率或倍率等数值。";
    if (port.kind === "condition") return "对应条件函数；下游输入端口求值时调用，返回布尔值。";
    if (port.kind === "rarity") return "对应品质函数；下游输入端口求值时调用，返回品质配置值。";
    if (port.kind === "tag") return "对应标签函数；下游输入端口求值时调用，返回标签配置值。";
    return "对应运行时函数；函数返回该端口声明的数据类型。";
  }

  if (port.id === "flow") return "接收上游流程，收到后执行当前节点。";
  if (type === "eventEliminated" && port.id === "target") return "接收要监听的目标；目标被消除后触发事件。";
  if (type === "actionSpawn" && port.id === "position") return "接收生成位置候选；为空时由运行器选择可用位置。";
  if (type === "actionTransform" && port.id === "prototypes") return "接收转化后的目标原型，可多连，运行时从候选中选择。";
  if (type === "actionSwapPosition" && port.id === "positions") return "接收目标要交换到的位置候选。";
  if (type === "actionRarityWeight" && port.id === "rarity") return "接收要调整抽取权重的品质。";
  if (type === "filterPrototype" && port.id === "prototype") return "接收原型候选；筛选时比较实例的原型 ID。";
  if (type === "logicContains" && port.id === "targets") return "接收用于查找的目标数组。";
  if (type === "logicContains" && port.id === "target") return "接收要判断是否存在于数组中的目标。";
  if (port.id === "targets" || port.id === "target") return "接收目标数组。单个目标也按数组传递。";
  if (port.id === "amount") return "接收整数数量，用于指定动作数值或处理数量。";
  if (port.id === "multiplier") return "接收浮点倍率，倍率只表示额外倍率或后续产出倍率。";
  if (port.id === "condition") return "接收一个或多个布尔条件，全部成立时当前节点才执行。";
  if (port.id === "count") return "接收要保留、生成或处理的数量。";
  if (port.id === "min") return "接收随机范围最小值。";
  if (port.id === "max") return "接收随机范围最大值。";
  if (port.id === "left") return "接收计算或比较左值。";
  if (port.id === "right") return "接收计算或比较右值。";
  if (port.id === "rarity") return "接收品质常量，可多连时表示多个品质候选。";
  if (port.id === "tag") return "接收标签常量，可多连时表示多个标签候选。";
  if (port.id === "value") return "接收需要处理的数值。";
  return "接收运行时输入。";
}
